import { z } from 'zod';

import { checklistPersonSchema } from './event-checklist';
import { imageUrlSchema } from './store';

/**
 * Espaco dos eventos (docs/eventos, RF-E19 a RF-E21): a planta e as zonas.
 *
 *   - `/api/site/events/:id/space/...`  back-office (`events.manage`)
 *   - `/api/events/:slug/space`         site: so nome, cor e posicao das zonas a mostrar
 *
 * Posicoes em milesimas da planta (0 a 1000).
 */

export const ZONE_COLORS = {
  red: { label: 'Vermelho', hex: '#e5132b' },
  blue: { label: 'Azul', hex: '#3b82f6' },
  green: { label: 'Verde', hex: '#22c55e' },
  amber: { label: 'Amarelo', hex: '#eab308' },
  purple: { label: 'Roxo', hex: '#a855f7' },
  teal: { label: 'Turquesa', hex: '#14b8a6' },
  pink: { label: 'Rosa', hex: '#ec4899' },
  gray: { label: 'Cinzento', hex: '#9ca3af' },
} as const;

export const zoneColorSchema = z.enum([
  'red',
  'blue',
  'green',
  'amber',
  'purple',
  'teal',
  'pink',
  'gray',
]);
export type ZoneColor = z.infer<typeof zoneColorSchema>;

/** Largura e altura minimas de uma zona, em milesimas. */
export const ZONE_MIN_SIZE = 20;

const boxSchema = z.object({
  x: z.number().int().min(0).max(1000),
  y: z.number().int().min(0).max(1000),
  w: z.number().int().min(ZONE_MIN_SIZE).max(1000),
  h: z.number().int().min(ZONE_MIN_SIZE).max(1000),
});
export type ZoneBox = z.infer<typeof boxSchema>;

export const publicZoneSchema = boxSchema.extend({
  id: z.string().uuid(),
  name: z.string(),
  color: zoneColorSchema,
});
export type PublicZone = z.infer<typeof publicZoneSchema>;

export const publicEventSpaceSchema = z.object({
  floorPlanUrl: z.string().nullable(),
  zones: z.array(publicZoneSchema),
});
export type PublicEventSpace = z.infer<typeof publicEventSpaceSchema>;

export const eventZoneSchema = publicZoneSchema.extend({
  notes: z.string(),
  showOnSite: z.boolean(),
  responsibles: z.array(checklistPersonSchema),
});
export type EventZone = z.infer<typeof eventZoneSchema>;

export const eventSpaceSchema = z.object({
  eventId: z.string().uuid(),
  floorPlanUrl: z.string().nullable(),
  zones: z.array(eventZoneSchema),
});
export type EventSpace = z.infer<typeof eventSpaceSchema>;

export const floorPlanInputSchema = z.object({ floorPlanUrl: imageUrlSchema });

export const eventZoneInputSchema = boxSchema
  .extend({
    name: z.string().trim().min(1, 'da um nome a zona').max(60),
    color: zoneColorSchema.default('red'),
    notes: z.string().trim().max(1000).default(''),
    showOnSite: z.boolean().default(true),
    responsibleAccountIds: z.array(z.string().uuid()).max(10).default([]),
  })
  .refine((b) => b.x + b.w <= 1000 && b.y + b.h <= 1000, {
    message: 'a zona sai da planta',
    path: ['w'],
  });
export type EventZoneInput = z.input<typeof eventZoneInputSchema>;

export const spaceCopyInputSchema = z.object({ fromEventId: z.string().uuid() });
